import {
  OrganizationUnderwritingOverrideSummary,
  useLazyGetOrganizationsWithUnderwritingOverridesQuery,
} from '@/services/organizations';
import { Box, Button, Icon } from '@palmetto/palmetto-components';
import { useCallback } from 'react';

import { ProgramType, ProgramTypeLabels } from 'types';

interface OrganizationsWithOverridesProps {
  alias: string;
  programType: ProgramType;
}

const ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 2fr 1fr',
  gap: '8px',
};

function OrganizationOverrideRow({ org }: { org: OrganizationUnderwritingOverrideSummary }) {
  const handleOpen = useCallback(() => {
    window.open(`/settings/organizations/${org.alias}/underwriting`, '_blank', 'noopener');
  }, [org.alias]);

  return (
    <Box padding="sm md" alignItems="center" borderWidth="xs 0 0 0" borderColor="separator" style={ROW_STYLE}>
      <Box>
        <Box fontWeight="bold">{org.name}</Box>
        <Box color="grey-500" fontSize="xs">
          {org.alias}
        </Box>
      </Box>
      <Box>
        {org.hasGeneral ? <Icon name="check" size="sm" color="success" aria-label="General override" /> : null}
      </Box>
      <Box>{org.states.length ? org.states.join(', ') : '—'}</Box>
      <Box alignItems="flex-end">
        <Button size="sm" variant="secondary" tone="neutral" onClick={handleOpen}>
          Open underwriting
        </Button>
      </Box>
    </Box>
  );
}

export function OrganizationsWithOverrides({ alias, programType }: OrganizationsWithOverridesProps) {
  const [fetchOverrides, { data, isFetching, isError }] = useLazyGetOrganizationsWithUnderwritingOverridesQuery();

  const handleShow = useCallback(() => {
    fetchOverrides({ alias, programType });
  }, [fetchOverrides, alias, programType]);

  const orgs = data?.orgs;

  return (
    <Box margin="lg 0 0 0">
      <Box direction="row" justifyContent="space-between" alignItems="center" margin="0 0 sm 0">
        <Box fontWeight="bold" fontSize="md">
          {alias === 'sabal' ? 'Organizations with Overrides' : 'Child Organizations with Overrides'}
        </Box>
        <Button size="sm" variant="secondary" tone="neutral" onClick={handleShow} isLoading={isFetching}>
          {orgs ? 'Refresh' : 'Show organizations with overrides'}
        </Button>
      </Box>
      {isError && (
        <Box fontSize="sm" color="danger-500">
          Failed to load organizations with overrides.
        </Box>
      )}
      {orgs && orgs.length === 0 && (
        <Box fontSize="sm" color="grey-500" fontStyle="italic">
          No {alias === 'sabal' ? '' : 'child '}organizations have {ProgramTypeLabels[programType]} underwriting
          overrides.
        </Box>
      )}
      {orgs && orgs.length > 0 && (
        <Box fontSize="sm">
          <Box padding="sm md" color="grey-500" fontWeight="bold" style={ROW_STYLE}>
            <Box>Organization</Box>
            <Box>General Override</Box>
            <Box>State Overrides</Box>
            <Box />
          </Box>
          {orgs.map((org) => (
            <OrganizationOverrideRow key={org.alias} org={org} />
          ))}
        </Box>
      )}
    </Box>
  );
}
